/**
 * Unified API URL resolver for both HTTP and WebSocket connections
 * Ensures all API calls (REST + WS) use the same backend.
 *
 * Features: environment detection, health-probe URL, origin validation,
 * debug logging, CDN/asset origin helper, configurable production host.
 */

/** Production backend origin (override via VITE_PROD_BACKEND_ORIGIN) */
const PROD_BACKEND = import.meta.env.VITE_PROD_BACKEND_ORIGIN || 'https://api.gabhan.io';

/** Detect if running on localhost */
const _isLocalhost = () => {
  if (typeof window === 'undefined') return false;
  const h = window.location.hostname;
  return h === 'localhost' || h === '127.0.0.1' || h === '0.0.0.0' || h.startsWith('192.168.');
};

/** Detect current environment */
export function getEnvironment() {
  if (import.meta.env.MODE === 'test') return 'test';
  if (import.meta.env.MODE === 'production') return 'production';
  if (_isLocalhost()) return 'development';
  if (typeof window !== 'undefined' && window.location.protocol === 'https:') return 'production';
  return 'development';
}

/**
 * Get the base API URL for HTTP requests
 * - Dev: same origin (Vite proxy handles /api)
 * - Prod: configured production backend
 */
export function getAPIUrl() {
  // Explicit env var override (highest priority)
  if (import.meta.env.VITE_API_ORIGIN) {
    return import.meta.env.VITE_API_ORIGIN;
  }

  if (typeof window !== 'undefined') {
    if (_isLocalhost()) {
      // Dev: use same origin so Vite proxy handles /api
      return window.location.origin;
    }

    // Production: always use configured backend (never localhost)
    if (window.location.protocol === 'https:') {
      return PROD_BACKEND;
    }
  }

  // Fallback
  return 'http://localhost:5050';
}

/**
 * Get the WebSocket URL for Socket.IO
 * In dev: use same origin so Vite proxy handles the upgrade
 * In prod: connect directly to backend
 */
export function getWebSocketUrl() {
  // Explicit env var override
  if (import.meta.env.VITE_SOCKET_ORIGIN) {
    return import.meta.env.VITE_SOCKET_ORIGIN;
  }

  if (typeof window !== 'undefined') {
    if (_isLocalhost()) {
      return window.location.origin;
    }

    if (window.location.protocol === 'https:') {
      return PROD_BACKEND;
    }
  }

  return 'http://localhost:5050';
}

/**
 * Get the health-check URL (for connectivity probes)
 */
export function getHealthUrl() {
  return `${getAPIUrl()}/health`;
}

/**
 * Build a full API endpoint path
 * @param {string} path - e.g. '/products' or 'products'
 * @returns {string} Full URL: '<origin>/api/products'
 */
export function buildApiUrl(path) {
  const base = getAPIUrl();
  const normalized = path.startsWith('/') ? path : `/${path}`;
  const withApi = normalized.startsWith('/api') ? normalized : `/api${normalized}`;
  return `${base}${withApi}`;
}

/**
 * Get CDN / static asset origin (if configured)
 * Falls back to API origin
 */
export function getAssetUrl() {
  if (import.meta.env.VITE_CDN_ORIGIN) {
    return import.meta.env.VITE_CDN_ORIGIN;
  }
  return getAPIUrl();
}

/**
 * Validate that an origin is in the allow-list
 * Prevents accidental requests to untrusted hosts
 * @param {string} origin
 * @returns {boolean}
 */
export function isAllowedOrigin(origin) {
  const allowed = [
    'http://localhost:5050',
    'http://localhost:5173',
    PROD_BACKEND,
  ];

  // Add any custom allowed origins from env
  const extra = import.meta.env.VITE_ALLOWED_ORIGINS;
  if (extra) {
    extra.split(',').forEach((o) => allowed.push(o.trim()));
  }

  return allowed.some((a) => origin.startsWith(a));
}

/**
 * Get a debug snapshot of resolved URLs (for support/diagnostics)
 */
export function getUrlDebugInfo() {
  return {
    apiUrl: getAPIUrl(),
    wsUrl: getWebSocketUrl(),
    healthUrl: getHealthUrl(),
    assetUrl: getAssetUrl(),
    environment: getEnvironment(),
    isLocalhost: _isLocalhost(),
    windowOrigin: typeof window !== 'undefined' ? window.location.origin : 'N/A',
    prodBackend: PROD_BACKEND,
    overrides: {
      VITE_API_ORIGIN: import.meta.env.VITE_API_ORIGIN || null,
      VITE_SOCKET_ORIGIN: import.meta.env.VITE_SOCKET_ORIGIN || null,
      VITE_CDN_ORIGIN: import.meta.env.VITE_CDN_ORIGIN || null,
    },
  };
}
